// Map search (spec 15c §3.6): systems by name or generated name, commanders by name, on the
// already loaded map. Prefix matches first; at most 3 commanders, then systems up to the limit.
import type { MapSystem } from '@stars-and-ships/api-types';

export const SEARCH_MIN_CHARS = 2;
const MAX_COMMANDERS = 3;

export type MapSearchResult =
  | { kind: 'system'; systemId: string; name: string; ownerName: string | null }
  | {
      kind: 'commander';
      playerId: string;
      name: string;
      tag: string | null;
      systemCount: number;
      firstSystemId: string;
    };

type CommanderResult = Extract<MapSearchResult, { kind: 'commander' }>;
type Ranked<T> = { rank: number; name: string; result: T };
const byRank = <T>(a: Ranked<T>, b: Ranked<T>) => a.rank - b.rank || a.name.localeCompare(b.name);

export function searchMap(
  systems: readonly MapSystem[],
  query: string,
  limit = 8
): MapSearchResult[] {
  const q = query.trim().toLowerCase();
  if (q.length < SEARCH_MIN_CHARS) return [];
  const rank = (...texts: string[]) => {
    const hits = texts.map((t) => t.toLowerCase().indexOf(q)).filter((i) => i >= 0);
    if (hits.length === 0) return -1;
    return hits.includes(0) ? 0 : 1;
  };
  const found: Ranked<MapSearchResult>[] = [];
  const commanders = new Map<string, Ranked<CommanderResult>>();
  for (const s of systems) {
    const r = rank(s.name, s.generatedName);
    if (r >= 0) {
      found.push({
        rank: r,
        name: s.name,
        result: {
          kind: 'system',
          systemId: s.id,
          name: s.name,
          ownerName: s.owner?.displayName ?? null,
        },
      });
    }
    if (!s.owner) continue;
    const known = commanders.get(s.owner.playerId);
    if (known) {
      known.result.systemCount += 1;
      continue;
    }
    const cr = rank(s.owner.displayName);
    if (cr < 0) continue;
    commanders.set(s.owner.playerId, {
      rank: cr,
      name: s.owner.displayName,
      result: {
        kind: 'commander',
        playerId: s.owner.playerId,
        name: s.owner.displayName,
        tag: s.owner.alliance?.tag ?? null,
        systemCount: 1,
        firstSystemId: s.id,
      },
    });
  }
  const people = [...commanders.values()]
    .sort(byRank)
    .slice(0, MAX_COMMANDERS)
    .map((c) => c.result);
  return [...people, ...found.sort(byRank).map((f) => f.result)].slice(0, limit);
}
